import { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { ConsultThread } from '../../components/medical/ConsultThread';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Salad, Baby, HeartPulse, Lock, ShieldCheck, Star, Loader2, ArrowLeft, RotateCcw, Video } from 'lucide-react';
import { toast } from 'sonner';

const DISCLAIMER = "This is a second opinion for general guidance — not a substitute for your primary doctor or emergency care. Our team does not prescribe medicines.";

export default function Nutritionist() {
  const { user } = useAuth();
  const [avail, setAvail] = useState(null);
  const [active, setActive] = useState(null);
  const [recent, setRecent] = useState([]);
  const [consent, setConsent] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [rateFor, setRateFor] = useState(null);
  const [activeMeta, setActiveMeta] = useState(null);
  const [upcomingCall, setUpcomingCall] = useState(null);

  const isElite = (user?.learner_type || '').toLowerCase() === 'elite';

  const load = async () => {
    try {
      const [a, m] = await Promise.all([api.get('/api/medical/availability'), api.get('/api/medical/my-consults')]);
      setAvail(a.data); setActive(m.data.active); setRecent(m.data.recent || []); setUpcomingCall(m.data.upcoming_call || null);
    } catch (e) { /* */ }
  };
  useEffect(() => {
    Promise.all([api.get('/api/medical/availability'), api.get('/api/medical/my-consults')])
      .then(([a, m]) => { setAvail(a.data); setActive(m.data.active); setRecent(m.data.recent || []); setUpcomingCall(m.data.upcoming_call || null); })
      .catch(() => {});
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/api/medical/connect', { concern: 'nutrition' });
      toast.success('Connected — our nutritionist will reply shortly.');
      setConsent(false); await load();
      setActive(a => a || { id: res.data.id, status: res.data.status });
    } catch (e) {
      const code = e.response?.data?.detailObject?.code;
      if (code === 'NUTRITIONIST_UNAVAILABLE') toast.info('Our nutritionist is currently unavailable. SoulAI can still help in the meantime.');
      else toast.error(e.response?.data?.detail || 'Could not connect');
      load();
    } finally { setConnecting(false); }
  };

  const reopen = async (id) => {
    try { await api.post(`/api/medical/consult/${id}/reopen`); toast.success('Reopened — the nutritionist has been notified.'); setActiveMeta(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not reopen'); }
  };

  const nutriAvailable = avail?.nutritionist?.available;

  // ── Active conversation view ──
  if (active) {
    const status = activeMeta?.status || active.status;
    const closed = status === 'completed' || status === 'closed';
    const statusLabel = status === 'waiting' ? 'Waiting for the nutritionist to join…' : closed ? 'This chat is closed' : 'Connected';
    return (
      <div className="max-w-2xl mx-auto space-y-3">
        <button onClick={() => { setActive(null); setActiveMeta(null); }} className="text-sm text-muted-foreground flex items-center gap-1" data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
        <Card className="rounded-2xl overflow-hidden">
          <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><Salad className="h-5 w-5" /><div><p className="font-medium text-sm">{activeMeta?.doctor_name || 'Nutrition consultation'}</p><p className="text-[11px] text-emerald-100">{statusLabel}</p></div></div>
          </div>
          <div className="h-[58vh] min-h-[400px]">
            <ConsultThread consultId={active.id} composer={!closed} onMeta={(m) => {
              setActiveMeta(m);
              if (m?.status === 'completed' && rateFor !== active.id && !m?.rating) setRateFor(active.id);
            }} />
          </div>
          {closed && (
            <div className="p-3 border-t bg-muted/30 flex flex-wrap gap-2" data-testid="learner-closed-actions">
              {nutriAvailable && isElite && <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => reopen(active.id)} data-testid="reopen-active-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen chat</Button>}
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setRateFor(active.id)} data-testid="rate-active-btn"><Star className="h-3.5 w-3.5 mr-1" />Rate session</Button>
            </div>
          )}
        </Card>
        <p className="text-[11px] text-muted-foreground text-center">{DISCLAIMER}</p>
        <RateDialog id={rateFor} onDone={() => { setRateFor(null); load(); }} />
      </div>
    );
  }

  // ── Landing: provider tiles ──
  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">Talk to an expert</h1>
        <p className="text-sm text-muted-foreground">Get a second opinion from our medical team.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {/* Nutritionist — active */}
        <Card className={`rounded-2xl ${nutriAvailable && isElite ? 'border-emerald-300' : 'opacity-90'}`} data-testid="tile-nutritionist">
          <CardContent className="pt-5 pb-4 text-center">
            <div className="h-11 w-11 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2"><Salad className="h-5 w-5 text-emerald-600" /></div>
            <p className="font-medium text-sm">Nutritionist</p>
            <p className="text-[11px] text-muted-foreground mb-2">Diet & nutrition guidance</p>
            {!isElite ? (
              <Button size="sm" variant="outline" className="w-full" onClick={() => toast.info('Live consultations are an Elite benefit.')} data-testid="upsell-btn"><Lock className="h-3.5 w-3.5 mr-1" />Elite only</Button>
            ) : nutriAvailable ? (
              <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setConsent(true)} data-testid="connect-nutritionist-btn">Connect — free</Button>
            ) : (
              <Button size="sm" variant="outline" className="w-full" disabled data-testid="nutritionist-unavailable">Currently unavailable</Button>
            )}
          </CardContent>
        </Card>
        {/* Gynae / Paeds — coming soon */}
        {[{ icon: HeartPulse, label: 'Gynaecologist', sub: 'Prenatal & postnatal' }, { icon: Baby, label: 'Paediatrician', sub: 'Newborn & baby care' }].map(t => (
          <Card key={t.label} className="rounded-2xl opacity-60" data-testid="tile-coming-soon">
            <CardContent className="pt-5 pb-4 text-center">
              <div className="h-11 w-11 rounded-full bg-muted flex items-center justify-center mx-auto mb-2"><t.icon className="h-5 w-5 text-muted-foreground" /></div>
              <p className="font-medium text-sm">{t.label}</p>
              <p className="text-[11px] text-muted-foreground mb-2">{t.sub}</p>
              <Button size="sm" variant="outline" className="w-full" disabled>Coming soon</Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {!nutriAvailable && isElite && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="unavailable-note">Our nutritionist is currently on leave. SoulAI can still answer your nutrition questions in the meantime.</p>
      )}

      {upcomingCall && <UpcomingCallCard call={upcomingCall} onOpen={() => setActive({ id: upcomingCall.consult_id, status: 'active' })} />}

      {/* Recent consultations */}
      {recent.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Past consultations</p>
          {recent.map(r => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-xl border bg-white text-sm" data-testid="past-consult-row">
              <div><p className="font-medium">{r.provider_name || 'Nutritionist'}</p><p className="text-xs text-muted-foreground">{r.concern} · {r.last_closed_at ? `closed ${new Date(r.last_closed_at).toLocaleDateString()}` : r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}{r.episodes_count > 1 ? ` · ${r.episodes_count} conversations` : ''}{r.rating?.score_out_of_10 ? ` · ★${r.rating.score_out_of_10}/10` : ''}</p></div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setActive({ id: r.id, status: 'completed' })} data-testid="view-consult-btn">View</Button>
                {nutriAvailable && isElite && <Button size="sm" variant="outline" onClick={() => reopen(r.id)} data-testid="reopen-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reconnect</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Consent dialog */}
      <Dialog open={consent} onOpenChange={setConsent}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />Before we connect you</DialogTitle><DialogDescription>Please review how we&apos;ll share your information with the nutritionist.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>To help our nutritionist give you the best guidance, we&apos;ll share your recent chat context{' '}and any documents you choose to attach.</p>
            <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">{DISCLAIMER}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConsent(false)}>Cancel</Button>
            <Button onClick={connect} disabled={connecting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="consent-continue-btn">{connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UpcomingCallCard({ call, onOpen }) {
  const when = call.scheduled_at ? new Date(call.scheduled_at).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '';
  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 flex items-center justify-between gap-3" data-testid="upcoming-consult-call-card">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center shrink-0"><Video className="h-5 w-5 text-sky-700" /></div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{call.title}</p>
          <p className="text-xs text-sky-800" data-testid="upcoming-consult-call-when">{when} · {call.duration_minutes} min</p>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" variant="ghost" onClick={onOpen} data-testid="upcoming-consult-call-open-chat">Open chat</Button>
        <a href={call.join_url} target="_blank" rel="noopener noreferrer" data-testid="upcoming-consult-call-join"><Button size="sm" className="bg-sky-600 hover:bg-sky-700"><Video className="h-3.5 w-3.5 mr-1" />Join</Button></a>
      </div>
    </div>
  );
}

function RateDialog({ id, onDone }) {
  const [score, setScore] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!score) { onDone(); return; }
    setBusy(true);
    try { await api.post(`/api/medical/consult/${id}/rate`, { score_out_of_10: score, review_text: text }); toast.success('Thanks for your feedback!'); }
    catch { /* */ } finally { setBusy(false); onDone(); }
  };
  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onDone()}>
      <DialogContent>
        <DialogHeader><DialogTitle>How was your consultation?</DialogTitle><DialogDescription>Your rating helps us improve. This is optional — you can skip it.</DialogDescription></DialogHeader>
        <div className="flex justify-center gap-1 py-2" data-testid="rating-stars">
          {Array.from({ length: 10 }).map((_, i) => (
            <button key={i} onClick={() => setScore(i + 1)}><Star className={`h-5 w-5 ${i < score ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} /></button>
          ))}
        </div>
        <Textarea placeholder="Anything you'd like to share? (optional)" value={text} onChange={e => setText(e.target.value)} rows={3} />
        <DialogFooter>
          <Button variant="ghost" onClick={onDone} data-testid="skip-rating-btn">Skip</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-rating-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
